import React, { useMemo, useState } from 'react';
import { Box, Typography } from '@mui/material';
import BarChartIcon from '@mui/icons-material/BarChart';
import CloseIcon from '@mui/icons-material/Close';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import Inventory2OutlinedIcon from '@mui/icons-material/Inventory2Outlined';
import LocalShippingOutlinedIcon from '@mui/icons-material/LocalShippingOutlined';
import MenuBookOutlinedIcon from '@mui/icons-material/MenuBookOutlined';
import SearchIcon from '@mui/icons-material/Search';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import { colors, typography } from '../constants';
import {
  Card,
  CenteredLoader,
  ChannelDot,
  MarginBar,
  NumberField,
  ScreenState,
  Toggle,
} from '../components';
import {
  seedFirstPage,
  useAnalytics,
  useMenuItemsPages,
  useUpdateMenuItem,
} from '../hooks/useProfitData';
import { useDebouncedValue } from '../hooks/useDebouncedValue';
import { fmt, fmtD, fmtN, negativeTone, pct, pctVal } from '../lib/format';
import { itemChannelRows } from '../lib/itemChannels';
import { netColor } from './Overview';

/** The cost basis behind the figure, kept in place but not shown. */
const SHOW_COST_BASIS_HINT = false;

const SEARCH_DEBOUNCE_MS = 300;

const statCardSx = {
  flex: 1,
  minWidth: 150,
  backgroundColor: colors.card,
  borderRadius: 2.5,
  p: 1.25,
  border: `1px solid ${colors.border}`,
};

const statLabelSx = {
  fontSize: typography.caption,
  color: colors.faint,
  fontWeight: 600,
  textTransform: 'uppercase',
  mb: 0.5,
};

const statValueSx = {
  fontSize: typography.heading,
  fontWeight: 800,
  color: colors.foreground,
};

const statHintSx = {
  fontSize: typography.caption,
  color: colors.faint,
  mt: 0.5,
};

const gmRowSx = {
  display: 'flex',
  justifyContent: 'space-between',
  mb: 0.5,
};

const gmLabelSx = { fontSize: typography.caption, color: colors.mutedForeground };

const gmValueSx = {
  fontSize: typography.caption,
  fontWeight: 700,
  color: colors.mutedForeground,
};

function ItemRow({ item, channels, operatingCostPct }) {
  const updateItem = useUpdateMenuItem();
  const [expanded, setExpanded] = useState(false);
  const [mode, setMode] = useState('dollar');
  const [suggDismissed, setSuggDismissed] = useState(false);

  const nc = netColor(item.netProfitPct);
  const curD = item.fcd;
  const suggD = item.suggestedCostDollar;
  // With no menu price and no sales there is nothing to charge a percentage
  // against, so a dollar figure cannot be expressed as a food cost at all.
  const hasCostBasis = item.costBasis > 0;
  // A stock-tracked item costs what it was bought for. The engine charges that
  // price and never the percentage, so the cost is reported here rather than
  // offered as an edit that would change nothing.
  const inventoryPriced = item.costSource === 'inventory';
  const showSugg = expanded && !suggDismissed && hasCostBasis && !inventoryPriced;
  // The share of the price this item actually costs, which for a stock price is
  // not the percentage saved on the item.
  const costPct = inventoryPriced
    ? hasCostBasis
      ? (curD / item.costBasis) * 100
      : 0
    : item.foodCostPct;

  // A dollar cost is stored as a percentage of `costBasis`, the same figure the
  // engine charges the percentage against, so the dollars typed in come back
  // out unchanged.
  const commit = (raw) => {
    const v = parseFloat(raw);
    if (isNaN(v) || v <= 0) return;
    if (mode === 'dollar' && !hasCostBasis) return;
    const foodCostPct = mode === 'dollar' ? (v / item.costBasis) * 100 : v;
    updateItem.mutate({ id: item.id, body: { foodCostPct } });
  };

  const acceptSuggestion = () => {
    const foodCostPct = (suggD / item.costBasis) * 100;
    updateItem.mutate({ id: item.id, body: { foodCostPct } });
    setSuggDismissed(true);
  };

  // What a unit nets on each channel. Derived rather than read off the item:
  // `/analytics` sends one card per menu item, and these rows on all of them
  // were two thirds of the response.
  const chRows = useMemo(
    () => itemChannelRows(item, channels, operatingCostPct),
    [item, channels, operatingCostPct]
  );

  // Delivery channels (commission-bearing), for the delivery-margin tip.
  const deliveryChs = chRows.filter((c) => c.commission > 0);
  const worstDelivery = deliveryChs.reduce(
    (worst, c) => (!worst || c.profitPct < worst.profitPct ? c : worst),
    null
  );

  const banner =
    item.netProfitPct >= 15
      ? colors.successSurface
      : item.netProfitPct >= 5
        ? colors.warningSurface
        : colors.destructiveSurface;

  return (
    <Card sx={{ p: 0, overflow: 'hidden' }}>
      <Box
        component="button"
        type="button"
        onClick={() => setExpanded((e) => !e)}
        sx={{
          width: '100%',
          px: 2,
          py: 1.75,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 1.25,
          border: 'none',
          backgroundColor: 'transparent',
          cursor: 'pointer',
          fontFamily: 'inherit',
          textAlign: 'left',
          '&:hover': { backgroundColor: colors.muted },
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1.25,
            flex: 1,
            minWidth: 0,
          }}
        >
          <Typography sx={{ fontSize: typography.display }}>
            {item.emoji}
          </Typography>
          <Box sx={{ minWidth: 0 }}>
            <Typography
              sx={{
                fontWeight: 700,
                fontSize: typography.small,
                color: colors.foreground,
              }}
            >
              {item.name}
            </Typography>
            <Typography
              sx={{
                fontSize: typography.caption,
                color: colors.mutedForeground,
                mt: '1px',
              }}
            >
              {fmtN(item.unitsSold)} sold · {fmt(item.salesPrice * item.unitsSold)}
            </Typography>
          </Box>
        </Box>

        <Box sx={{ textAlign: 'right', flexShrink: 0 }}>
          <Typography
            sx={{ fontSize: typography.body, fontWeight: 800, color: nc }}
          >
            {pct(item.netProfitPct)}
          </Typography>
          <Typography
            sx={{ fontSize: typography.caption, color: colors.faint }}
          >
            net margin
          </Typography>
          {expanded ? (
            <ExpandLessIcon sx={{ fontSize: 16, color: colors.primary }} />
          ) : (
            <ExpandMoreIcon sx={{ fontSize: 16, color: colors.faint }} />
          )}
        </Box>
      </Box>

      {expanded ? (
        <Box
          sx={{
            borderTop: `1px solid ${colors.border}`,
            p: 2,
            backgroundColor: colors.muted,
          }}
        >
          {showSugg ? (
            <Box
              sx={{
                backgroundColor: colors.accent,
                borderRadius: 2.5,
                p: 1.5,
                mb: 1.75,
                display: 'flex',
                flexDirection: 'column',
                gap: 1,
              }}
            >
              <Box sx={{ display: 'flex', gap: 0.75 }}>
                <TrendingUpIcon
                  sx={{
                    fontSize: 16,
                    color: colors.accentForeground,
                    mt: '1px',
                    flexShrink: 0,
                  }}
                />
                <Typography
                  sx={{
                    flex: 1,
                    fontSize: typography.caption,
                    lineHeight: 1.5,
                    color: colors.accentForeground,
                  }}
                >
                  <b>Orders.co avg: </b>
                  Similar restaurants pay ~<b>{fmtD(suggD)}</b> (
                  {pct((suggD / item.costBasis) * 100)}) for this item.
                  {Math.abs(curD - suggD) < 0.1
                    ? " You're right on the average."
                    : curD > suggD
                      ? ` You may be over by ${fmtD(curD - suggD)}/item (~${fmt(
                          (curD - suggD) * item.unitsSold
                        )} this period).`
                      : ` You're ${fmtD(suggD - curD)}/item below — nice margin!`}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Box
                  component="button"
                  type="button"
                  onClick={acceptSuggestion}
                  sx={{
                    flex: 1,
                    py: 1,
                    border: 'none',
                    borderRadius: 1.75,
                    cursor: 'pointer',
                    fontFamily: 'inherit',
                    fontSize: typography.caption,
                    fontWeight: 700,
                    backgroundColor: colors.primary,
                    color: colors.primaryForeground,
                  }}
                >
                  ✓ Use {fmtD(suggD)}
                </Box>
                <Box
                  component="button"
                  type="button"
                  onClick={() => setSuggDismissed(true)}
                  sx={{
                    flex: 1,
                    py: 1,
                    borderRadius: 1.75,
                    cursor: 'pointer',
                    fontFamily: 'inherit',
                    fontSize: typography.caption,
                    backgroundColor: colors.card,
                    border: `1px solid ${colors.border}`,
                    color: colors.mutedForeground,
                  }}
                >
                  Keep mine
                </Box>
              </Box>
            </Box>
          ) : null}

          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              mb: 1.25,
              gap: 1,
            }}
          >
            <Typography
              sx={{
                fontSize: typography.caption,
                fontWeight: 700,
                color: colors.foreground,
              }}
            >
              Food Cost
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {!inventoryPriced ? (
                <Box
                  component="button"
                  type="button"
                  onClick={() => setSuggDismissed(false)}
                  sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 0.4,
                    py: 0.5,
                    px: 1.1,
                    cursor: 'pointer',
                    fontFamily: 'inherit',
                    fontSize: typography.caption,
                    fontWeight: 600,
                    color: colors.primary,
                    backgroundColor: colors.accent,
                    border: `1px solid ${colors.border}`,
                    borderRadius: 2,
                  }}
                >
                  <BarChartIcon sx={{ fontSize: 14 }} />
                  Compare
                </Box>
              ) : null}
              <Toggle
                value={mode}
                onChange={setMode}
                options={[
                  { value: 'pct', label: '%' },
                  { value: 'dollar', label: '$' },
                ]}
              />
            </Box>
          </Box>

          <Box sx={{ display: 'flex', gap: 1.25, mb: 1.75, flexWrap: 'wrap' }}>
            <Box sx={statCardSx}>
              <Typography sx={statLabelSx}>Menu Price</Typography>
              <Typography sx={statValueSx}>{fmtD(item.price)}</Typography>
              <Typography sx={statHintSx}>
                {item.unitsSold > 0
                  ? `sold at ${fmtD(item.salesPrice)}`
                  : 'no sales'}
              </Typography>
            </Box>

            <Box
              sx={{
                ...statCardSx,
                border: `2px solid ${colors.primary}`,
              }}
            >
              <Typography sx={{ ...statLabelSx, color: colors.primary }}>
                {mode === 'dollar' ? 'Cost $' : 'Cost %'}
              </Typography>
              {inventoryPriced ? (
                <Typography sx={{ ...statValueSx, color: colors.primary }}>
                  {mode === 'dollar' ? fmtD(curD) : pct(costPct)}
                </Typography>
              ) : (
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  {mode === 'dollar' ? (
                    <Typography
                      sx={{
                        fontSize: typography.heading,
                        fontWeight: 800,
                        color: colors.primary,
                      }}
                    >
                      $
                    </Typography>
                  ) : null}
                  <NumberField
                    key={`${item.id}-${mode}`}
                    initialValue={
                      mode === 'dollar'
                        ? parseFloat(curD.toFixed(2))
                        : pctVal(item.foodCostPct)
                    }
                    onCommit={commit}
                    width={56}
                    fontSize={18}
                    align="left"
                  />
                  {mode === 'pct' ? (
                    <Typography
                      sx={{
                        fontSize: typography.heading,
                        fontWeight: 800,
                        color: colors.primary,
                      }}
                    >
                      %
                    </Typography>
                  ) : null}
                </Box>
              )}
              {SHOW_COST_BASIS_HINT ? (
                <Typography sx={statHintSx}>
                  {mode === 'dollar'
                    ? `${pct(costPct)} of ${fmtD(item.costBasis)}`
                    : `${fmtD(curD)}/item`}
                </Typography>
              ) : null}
              {inventoryPriced ? (
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.5,
                    mt: 0.25,
                  }}
                >
                  <Inventory2OutlinedIcon
                    sx={{ fontSize: 13, color: colors.mutedForeground }}
                  />
                  <Typography
                    sx={{
                      fontSize: typography.caption,
                      color: colors.mutedForeground,
                      fontWeight: 600,
                    }}
                  >
                    From inventory
                  </Typography>
                </Box>
              ) : null}
            </Box>

            <Box
              sx={{
                ...statCardSx,
                backgroundColor: colors.successSurface,
                border: `1px solid ${colors.successSurface}`,
              }}
            >
              <Typography
                sx={{
                  ...statLabelSx,
                  color: colors.successSurfaceForeground,
                }}
              >
                Gross Profit
              </Typography>
              <Typography
                sx={{
                  ...statValueSx,
                  color: colors.successSurfaceForeground,
                  ...negativeTone(item.grossProfit),
                }}
              >
                {fmtD(item.grossProfit)}
              </Typography>
              <Typography
                sx={{ ...statHintSx, ...negativeTone(item.grossMarginPct) }}
              >
                {pct(item.grossMarginPct)}
              </Typography>
            </Box>
          </Box>

          <Box sx={{ mb: 1.75 }}>
            <Box sx={gmRowSx}>
              <Typography sx={gmLabelSx}>Gross margin</Typography>
              <Typography
                sx={{ ...gmValueSx, ...negativeTone(item.grossMarginPct) }}
              >
                {pct(item.grossMarginPct)}
              </Typography>
            </Box>
            <MarginBar value={item.grossMarginPct} />
          </Box>

          {/* The net margin above is after this fee, so the card says what it
              was and which mix it came from. */}
          {item.providerFee > 0 ? (
            <Box sx={{ ...gmRowSx, mb: 1.75 }}>
              <Typography sx={gmLabelSx}>
                {`Provider fees · ${pct(item.providerFeePct)} of sales mix`}
              </Typography>
              <Typography sx={{ ...gmValueSx, color: colors.destructive }}>
                {fmtD(-item.providerFee, true)}
              </Typography>
            </Box>
          ) : null}

          <Typography
            sx={{
              fontSize: typography.caption,
              fontWeight: 700,
              color: colors.foreground,
              mb: 1,
            }}
          >
            Profit per item by channel
          </Typography>
          {chRows.map((ch) => {
            const cc =
              ch.profit > 0
                ? ch.profitPct >= 15
                  ? colors.success
                  : colors.warning
                : colors.destructive;
            return (
              <Box
                key={ch.channelId}
                sx={{ display: 'flex', alignItems: 'center', mb: 0.9, gap: 1 }}
              >
                <ChannelDot color={ch.color} size={8} />
                <Typography
                  noWrap
                  sx={{
                    flex: 1,
                    fontSize: typography.caption,
                    color: colors.foreground,
                  }}
                >
                  {ch.name}
                </Typography>
                {ch.commission > 0 ? (
                  <Typography
                    sx={{
                      fontSize: typography.caption,
                      color: colors.destructive,
                    }}
                  >
                    {pct(-ch.commission)}
                  </Typography>
                ) : null}
                <Typography
                  sx={{
                    fontSize: typography.caption,
                    fontWeight: 700,
                    color: cc,
                    minWidth: 62,
                    textAlign: 'right',
                  }}
                >
                  {fmtD(ch.profit, true)}
                </Typography>
                <Typography
                  sx={{
                    fontSize: typography.caption,
                    color: colors.faint,
                    minWidth: 52,
                    textAlign: 'right',
                    ...negativeTone(ch.profitPct),
                  }}
                >
                  {pct(ch.profitPct)}
                </Typography>
              </Box>
            );
          })}

          <Box
            sx={{
              mt: 1.5,
              borderRadius: 2,
              p: 1.5,
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              backgroundColor: banner,
            }}
          >
            <Typography
              sx={{ fontSize: typography.caption, color: colors.foreground }}
            >
              Monthly profit estimate
            </Typography>
            <Typography
              sx={{ fontSize: typography.emphasis, fontWeight: 800, color: nc }}
            >
              {fmt(item.totalProfit)}
            </Typography>
          </Box>

          {worstDelivery ? (
            <Box
              sx={{
                mt: 1.25,
                display: 'flex',
                alignItems: 'flex-start',
                gap: 0.75,
                backgroundColor: colors.warningSurface,
                borderRadius: 2,
                p: 1.25,
              }}
            >
              <LocalShippingOutlinedIcon
                sx={{
                  fontSize: 15,
                  color: colors.warningSurfaceForeground,
                  flexShrink: 0,
                }}
              />
              <Typography
                sx={{
                  flex: 1,
                  fontSize: typography.caption,
                  color: colors.warningSurfaceForeground,
                  lineHeight: 1.35,
                }}
              >
                On {worstDelivery.name} you net only {pct(worstDelivery.profitPct)}{' '}
                ({fmtD(worstDelivery.profit, true)}/item) after the{' '}
                {pct(worstDelivery.commission)} commission. A delivery-menu markup
                helps offset it.
              </Typography>
            </Box>
          ) : null}
        </Box>
      ) : null}
    </Card>
  );
}

/**
 * The menu list.
 *
 * Two reads, because the screen needs two different things. The dashboard
 * carries the window's channels and its operating percentage, which is what
 * prices a row's per-channel breakdown; the list itself is paged, ranked by
 * revenue on the server. It used to be one read of every item the business
 * sells, sorted here, to render the ten rows below — which on a large menu was
 * tens of megabytes for a screenful.
 */
export default function MenuItems() {
  const query = useAnalytics();
  // The dashboard read already carried the first page of this list whenever it
  // could be shown to be the same page, so the rows are on screen with the tab
  // and this endpoint is not called until the reader asks for more.
  const firstPage = useMemo(() => seedFirstPage(query.data), [query.data]);
  const [searchText, setSearchText] = useState('');
  const search = useDebouncedValue(searchText.trim(), SEARCH_DEBOUNCE_MS);
  // The dashboard's page is the unfiltered list's first page only, so a search
  // is read from the server rather than seeded with rows it did not match.
  const list = useMenuItemsPages({
    sort: 'revenue',
    order: 'desc',
    search: search || undefined,
    firstPage: search ? undefined : firstPage,
    firstPageFetchedAt: search ? undefined : query.dataUpdatedAt,
  });

  const items = useMemo(
    () => (list.data ? list.data.pages.flatMap((page) => page.items) : []),
    [list.data]
  );
  // Every page reports the same list length, so the first one that arrived
  // answers how many there are in total.
  const total = list.data?.pages[0]?.total ?? 0;

  return (
    <ScreenState
      id="menu"
      query={query}
      loadingLabel="Loading menu items…"
      emptyIcon={<MenuBookOutlinedIcon fontSize="inherit" />}
      emptyTitle="No menu items yet"
      emptyMessage="Import your sales data to see per-item food cost, margins and profit across every channel."
      allowNoData
    >
      {() => (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
          <Box sx={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
            <SearchIcon
              sx={{
                position: 'absolute',
                left: 12,
                fontSize: 18,
                color: colors.faint,
                pointerEvents: 'none',
              }}
            />
            <Box
              component="input"
              type="search"
              value={searchText}
              placeholder="Search items"
              autoComplete="off"
              spellCheck={false}
              onChange={(e) => setSearchText(e.target.value)}
              sx={{
                width: '100%',
                py: 1,
                pl: 4.5,
                pr: 4.5,
                fontSize: typography.small,
                fontFamily: 'inherit',
                color: colors.foreground,
                backgroundColor: colors.card,
                borderRadius: 2.5,
                border: `1px solid ${colors.border}`,
                outline: 'none',
                '&:focus': {
                  borderColor: colors.primary,
                  boxShadow: `0 0 0 3px ${colors.primary}33`,
                },
                '&::placeholder': { color: colors.faint },
                // The clear button below replaces the browser's own.
                '&::-webkit-search-cancel-button': { display: 'none' },
              }}
            />
            {searchText ? (
              <Box
                component="button"
                type="button"
                aria-label="Clear search"
                onClick={() => setSearchText('')}
                sx={{
                  position: 'absolute',
                  right: 8,
                  display: 'inline-flex',
                  p: 0.5,
                  border: 'none',
                  borderRadius: 1,
                  cursor: 'pointer',
                  backgroundColor: 'transparent',
                  color: colors.mutedForeground,
                  '&:hover': { backgroundColor: colors.muted },
                }}
              >
                <CloseIcon sx={{ fontSize: 16 }} />
              </Box>
            ) : null}
          </Box>

          <Box>
            <Typography
              sx={{
                fontSize: typography.caption,
                color: colors.mutedForeground,
                mb: 0.5,
              }}
            >
              {list.isPending ? null : search ? (
                <>
                  <b>{items.length}</b> of <b>{total}</b> items match “{search}”
                </>
              ) : (
                <>
                  Showing top <b>{items.length}</b> of <b>{total}</b> items
                </>
              )}
            </Typography>
            <Typography
              sx={{ fontSize: typography.caption, color: colors.faint }}
            >
              Open any item to adjust food cost. All channel profits update
              instantly.
            </Typography>
          </Box>

          {list.isPending ? (
            <CenteredLoader label="Loading items…" />
          ) : search && total === 0 ? (
            <Typography
              sx={{
                textAlign: 'center',
                fontSize: typography.caption,
                color: colors.mutedForeground,
                py: 3,
              }}
            >
              No items match “{search}”
            </Typography>
          ) : (
            items.map((item) => (
                <ItemRow
                  key={item.id}
                  item={item}
                  channels={query.data.channels}
                  operatingCostPct={query.data.operatingCostPct}
                />
              ))
          )}

          <Box sx={{ display: 'flex', gap: 1, mt: 0.5 }}>
            {list.hasNextPage ? (
              <Box
                component="button"
                type="button"
                disabled={list.isFetchingNextPage}
                onClick={() => list.fetchNextPage()}
                sx={{
                  flex: 1,
                  py: 1.25,
                  cursor: list.isFetchingNextPage ? 'default' : 'pointer',
                  fontFamily: 'inherit',
                  fontSize: typography.caption,
                  fontWeight: 600,
                  color: colors.primary,
                  backgroundColor: colors.card,
                  border: `1px solid ${colors.primary}`,
                  borderRadius: 2.5,
                }}
              >
                {list.isFetchingNextPage
                  ? 'Loading…'
                  : `+ Load ${Math.min(
                      list.data?.pages[0]?.limit ?? 0,
                      total - items.length
                    )} more items`}
              </Box>
            ) : null}
          </Box>

          {!list.hasNextPage && total > 0 ? (
            <Typography
              sx={{
                textAlign: 'center',
                fontSize: typography.caption,
                color: colors.faint,
                mt: 1,
              }}
            >
              All {total} items shown
            </Typography>
          ) : null}
        </Box>
      )}
    </ScreenState>
  );
}
